import { useId } from 'react';
import { useTheme } from 'styled-components';
import Button from '@hellodarwin/core/lib/components/common/button';
import HdTag from '@hellodarwin/core/lib/components/common/hd-tag';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Loading from '@hellodarwin/core/lib/components/loading';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import File from '@hellodarwin/icons/dist/icons/File';
import NewWindow from '@hellodarwin/icons/dist/icons/NewWindow';
import type { ProgramWorkingDocument } from '../../features/api/program-documents-api';
import {
  DocumentList,
  DocumentHeader,
  DocumentName,
  DocumentSection,
  DocumentSubtitle,
  WorkingCard,
  WorkingDocumentActions,
  WorkingDocumentDetails,
  WorkingDocumentIcon,
} from './styles';

const WorkingDocumentCard = ({
  document,
}: {
  document: ProgramWorkingDocument;
}) => {
  const { t } = useTranslations();
  const theme = useTheme();
  const typeTranslation = {
    google_docs: 'document-journey|workingDocumentGoogleDocs',
    google_sheets: 'document-journey|workingDocumentGoogleSheets',
    google_slides: 'document-journey|workingDocumentGoogleSlides',
    pdf: 'document-journey|workingDocumentPdf',
    file: 'document-journey|workingDocumentFile',
  }[document.document_type];

  return (
    <li>
      <WorkingCard
        href={document.url}
        target="_blank"
        rel="noopener noreferrer"
      >
        <WorkingDocumentIcon aria-hidden="true">
          <File size={24} />
        </WorkingDocumentIcon>
        <WorkingDocumentDetails>
          <DocumentName>{document.title}</DocumentName>
        </WorkingDocumentDetails>
        <WorkingDocumentActions>
          <HdTag
            text={t(typeTranslation)}
            color={theme.colors.purple_6}
            size="small"
          />
          <NewWindow size={20} aria-hidden="true" />
        </WorkingDocumentActions>
      </WorkingCard>
    </li>
  );
};

export const WorkingDocuments = ({
  documents,
  isLoading,
  isError,
  onRetry,
}: {
  documents: readonly ProgramWorkingDocument[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
}) => {
  const { t } = useTranslations();
  const titleId = useId();
  return (
    <DocumentSection aria-labelledby={titleId} aria-busy={isLoading}>
      <DocumentHeader>
        <Typography elementTheme="h5" id={titleId}>
          {t('document-journey|workingDocumentsTitle')}
        </Typography>
        <DocumentSubtitle>
          {t('document-journey|workingDocumentsDescription')}
        </DocumentSubtitle>
      </DocumentHeader>
      {isError ? (
        <>
          <div role="alert">
            <Typography>{t('document-journey|documentsLoadError')}</Typography>
          </div>
          {onRetry ? (
            <Button onClick={onRetry}>
              {t('document-journey|documentsRetry')}
            </Button>
          ) : null}
        </>
      ) : isLoading ? (
        <Loading />
      ) : documents.length === 0 ? (
        <Typography>{t('document-journey|workingDocumentsEmpty')}</Typography>
      ) : (
        <DocumentList>
          {documents.map((document) => (
            <WorkingDocumentCard
              key={`${document.id}:${document.url}`}
              document={document}
            />
          ))}
        </DocumentList>
      )}
    </DocumentSection>
  );
};
